import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, X, MessageCircle, Wand2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const SUGGESTIONS = [
    'What offers are available today?',
    'Gift under ₹5000?',
    'Where can I find Zara?',
    'Redeem points for movies?',
];

function sessionId() {
    let s = localStorage.getItem('io_concierge_session');
    if (!s) { s = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem('io_concierge_session', s); }
    return s;
}

export default function ConciergeWidget() {
    const { user, type } = useAuth();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const sid = useRef(sessionId()).current;
    const endRef = useRef(null);

    useEffect(() => {
        if (open && messages.length === 0) {
            api.get(`/concierge/history/${sid}`).then(({ data }) => setMessages(data)).catch(() => {});
        }
    }, [open, sid, messages.length]);

    useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy, open]);

    const send = async (text) => {
        const msg = text || input;
        if (!msg.trim() || busy) return;
        setInput('');
        setMessages((m) => [...m, { role: 'user', content: msg, id: `local-${Date.now()}` }]);
        setBusy(true);
        try {
            const { data } = await api.post('/concierge/chat', {
                session_id: sid, message: msg,
                customer_id: type === 'customer' ? user?.id : null,
            });
            setMessages((m) => [...m, { role: 'assistant', content: data.reply, id: data.message_id }]);
        } catch (e) {
            setMessages((m) => [...m, { role: 'assistant', content: 'I apologise — connectivity blip. Please try again.', id: `err-${Date.now()}` }]);
        } finally { setBusy(false); }
    };

    return (
        <>
            {/* Floating launcher */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="concierge-launcher"
                    className="fixed bottom-6 right-6 z-40 group"
                    aria-label="Open AI Concierge"
                >
                    <div className="relative">
                        <div className="absolute inset-0 bg-inorbit-yellow rounded-full opacity-60 blur-2xl group-hover:opacity-90 transition-opacity animate-pulse" />
                        <div className="relative bg-inorbit-yellow text-inorbit-blue w-16 h-16 rounded-full flex items-center justify-center shadow-[0_12px_40px_-8px_rgba(255,218,0,0.6)] hover:scale-[1.06] transition-transform border border-inorbit-blue/10">
                            <Wand2 size={24} strokeWidth={1.8} />
                        </div>
                        <div className="absolute -top-1 -right-1 bg-inorbit-orange text-white text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full shadow-md">AI</div>
                        <div className="absolute -bottom-7 right-0 text-[10px] tracking-[0.22em] uppercase font-semibold text-inorbit-blue whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                            Concierge
                        </div>
                    </div>
                </button>
            )}

            {/* Chat panel */}
            {open && (
                <div className="fixed bottom-6 right-6 z-40 w-[min(420px,calc(100vw-2rem))] h-[min(640px,calc(100vh-3rem))] glass-card rounded-2xl flex flex-col overflow-hidden page-in" data-testid="concierge-widget">
                    {/* Header */}
                    <div className="bg-inorbit-blue text-white p-5 flex items-center gap-3 relative overflow-hidden shrink-0 hairline-accent">
                        <div className="absolute -top-10 -right-10 w-32 h-32 border border-inorbit-yellow/20 rounded-full" />
                        <div className="absolute -bottom-12 -right-4 w-24 h-24 border border-inorbit-orange/20 rounded-full" />
                        <div className="relative w-10 h-10 bg-inorbit-yellow text-inorbit-blue rounded-full flex items-center justify-center shrink-0">
                            <Wand2 size={18} strokeWidth={1.8} />
                        </div>
                        <div className="relative flex-1 min-w-0">
                            <div className="font-serif text-lg leading-none">Inorbit One Concierge</div>
                            <div className="text-[10px] uppercase tracking-widest text-inorbit-yellow mt-1">
                                Powered by <span className="font-semibold">Fundle.ai</span>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="relative text-white/80 hover:text-inorbit-yellow" data-testid="concierge-close">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 bg-cream/40" data-testid="concierge-messages">
                        {messages.length === 0 && !busy && (
                            <div className="text-center py-6">
                                <div className="inline-block bg-inorbit-yellow text-inorbit-blue px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold mb-3">Try asking</div>
                                <div className="grid gap-2">
                                    {SUGGESTIONS.map((s) => (
                                        <button key={s} onClick={() => send(s)} className="text-left text-sm text-inorbit-blue bg-white border border-border-subtle hover:border-inorbit-orange hover:text-inorbit-orange transition-colors px-4 py-3 rounded-md" data-testid={`suggest-${s.slice(0,8)}`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="space-y-3">
                            {messages.map((m) => (
                                <div key={m.id} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
                                    {m.role === 'assistant' && (
                                        <div className="w-7 h-7 bg-inorbit-blue text-inorbit-yellow flex items-center justify-center shrink-0 rounded-full">
                                            <Sparkles size={12} strokeWidth={1.5} />
                                        </div>
                                    )}
                                    <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-inorbit-blue text-white rounded-br-sm' : 'bg-white border border-border-subtle text-inorbit-blue rounded-bl-sm'}`}>
                                        {m.content}
                                    </div>
                                </div>
                            ))}
                            {busy && (
                                <div className="flex gap-2">
                                    <div className="w-7 h-7 bg-inorbit-blue text-inorbit-yellow flex items-center justify-center shrink-0 rounded-full"><Sparkles size={12} strokeWidth={1.5} /></div>
                                    <div className="px-3 py-2 bg-white border border-border-subtle text-text-tertiary text-sm italic rounded-2xl rounded-bl-sm">Thinking…</div>
                                </div>
                            )}
                            <div ref={endRef} />
                        </div>
                    </div>

                    {/* Input */}
                    <div className="border-t border-border-subtle p-3 bg-white shrink-0">
                        <div className="flex items-center gap-2">
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && send()}
                                placeholder="Ask anything…"
                                disabled={busy}
                                className="flex-1 input-box py-2 text-sm"
                                data-testid="concierge-input"
                            />
                            <button className="btn-primary !px-4 !py-2.5" onClick={() => send()} disabled={!input.trim() || busy} data-testid="concierge-send">
                                <Send size={14} strokeWidth={1.8} />
                            </button>
                        </div>
                        <Link to="/concierge" className="block text-[10px] text-text-tertiary text-center mt-2 hover:text-inorbit-orange tracking-widest uppercase" data-testid="concierge-open-full">Open full conversation</Link>
                    </div>
                </div>
            )}
        </>
    );
}
